import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Card,
  Space,
  Typography,
  Tag,
  Spin,
  message,
  Button,
  Tooltip,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CalculatorOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

const { Title, Text } = Typography;
const { Search } = Input;

interface ICD10NTAPoint {
  id: string;
  code: string;
  nta_points: number;
}

interface NTAStats {
  count: number;
}

export const ICD10NTAPointsViewer: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [ntaPoints, setNtaPoints] = useState<ICD10NTAPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [stats, setStats] = useState<NTAStats | null>(null);
  const [versionInfo, setVersionInfo] = useState<any>(null);

  // Check if we're viewing a specific version
  const versionId = searchParams.get('version');

  const fetchNTAPoints = async (search = '', page = 1, pageSize = 50) => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      const searchParams = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
      });
      
      if (search) {
        searchParams.append('search', search);
      }

      // Use different endpoint based on whether we're viewing a specific version
      const endpoint = versionId 
        ? `${API_BASE_URL}/api/admin/data/icd10-nta-points/versions/${versionId}/data?${searchParams}`
        : `${API_BASE_URL}/api/admin/data/icd10-nta-points/points?${searchParams}`;

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch NTA points');
      }

      const data = await response.json();
      setNtaPoints(data);
    } catch (error) {
      console.error('Error fetching NTA points:', error);
      message.error('Failed to load NTA points');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (search = '') => {
    try {
      const token = await getAccessTokenSilently();
      
      // Use different endpoint based on whether we're viewing a specific version
      let endpoint = versionId 
        ? `${API_BASE_URL}/api/admin/data/icd10-nta-points/versions/${versionId}/data/count`
        : `${API_BASE_URL}/api/admin/data/icd10-nta-points/points/count`;

      // Add search parameter if provided (only for regular points, not version-specific)
      if (search && !versionId) {
        const searchParams = new URLSearchParams({ search });
        endpoint += `?${searchParams}`;
      }

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
      setPagination(prev => ({ ...prev, total: data.count || 0 }));
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchVersionInfo = async () => {
    if (!versionId) return;
    
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/admin/data/icd10-nta-points/versions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }

      const versions = await response.json();
      const version = versions.find((v: any) => v.id === versionId);
      setVersionInfo(version);
    } catch (error) {
      console.error('Error fetching version info:', error);
    }
  };

  useEffect(() => {
    fetchNTAPoints();
    fetchStats(''); // Pass empty string for initial load (no search)
    fetchVersionInfo();
  }, [versionId]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    
    // Use backend search
    fetchNTAPoints(value, 1, pagination.pageSize);
    fetchStats(value);
  };

  const handleTableChange = (paginationConfig: any) => {
    const { current, pageSize } = paginationConfig;
    setPagination(prev => ({ ...prev, current, pageSize }));
    fetchNTAPoints(searchText, current, pageSize);
  };

  const handleRefresh = () => {
    setSearchText('');
    fetchNTAPoints('', pagination.current, pagination.pageSize);
    fetchStats(''); // Pass empty string to clear search count
  };

  // Calculate some statistics
  const getPointsStats = () => {
    if (ntaPoints.length === 0) return null;
    
    const points = ntaPoints.map(p => p.nta_points);
    const maxPoints = Math.max(...points);
    const minPoints = Math.min(...points);
    const avgPoints = points.reduce((a, b) => a + b, 0) / points.length;
    
    return { maxPoints, minPoints, avgPoints };
  };

  const pointsStats = getPointsStats();

  const columns = [
    {
      title: 'ICD-10 Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      minWidth: 100,
      render: (code: string) => (
        <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {code}
        </Text>
      ),
      sorter: (a: ICD10NTAPoint, b: ICD10NTAPoint) => a.code.localeCompare(b.code),
    },
    {
      title: 'NTA Points',
      dataIndex: 'nta_points',
      key: 'nta_points',
      width: 110,
      minWidth: 90,
      render: (points: number) => {
        let color = 'default';
        if (points >= 75) color = 'red';
        else if (points >= 50) color = 'orange';
        else if (points >= 25) color = 'blue';
        else color = 'green';

        return (
          <Tag color={color} style={{ 
            fontWeight: 'bold', 
            minWidth: '50px', 
            textAlign: 'center',
            fontSize: '11px',
            padding: '2px 4px'
          }}>
            {points.toFixed(2)}
          </Tag>
        );
      },
      sorter: (a: ICD10NTAPoint, b: ICD10NTAPoint) => a.nta_points - b.nta_points,
      filters: [
        { text: 'High (75+)', value: 'high' },
        { text: 'Medium (50-74)', value: 'medium' },
        { text: 'Low (25-49)', value: 'low' },
        { text: 'Very Low (<25)', value: 'very-low' },
      ],
      onFilter: (value: any, record: ICD10NTAPoint) => {
        const points = record.nta_points;
        switch (value) {
          case 'high': return points >= 75;
          case 'medium': return points >= 50 && points < 75;
          case 'low': return points >= 25 && points < 50;
          case 'very-low': return points < 25;
          default: return true;
        }
      },
    },
    {
      title: 'Range',
      key: 'range',
      width: 100,
      minWidth: 80,
      render: (_: any, record: ICD10NTAPoint) => {
        const points = record.nta_points;
        let range = '';
        let color = '';
        
        if (points >= 75) {
          range = 'High';
          color = 'red';
        } else if (points >= 50) {
          range = 'Medium';
          color = 'orange';
        } else if (points >= 25) {
          range = 'Low';
          color = 'blue';
        } else {
          range = 'Very Low';
          color = 'green';
        }

        return <Tag color={color} style={{ fontSize: '11px', padding: '2px 4px' }}>{range}</Tag>;
      },
    },
  ];

  return (
    <div style={{ 
      padding: '24px', 
      maxWidth: '100%', 
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Title level={2} style={{ 
              margin: 0, 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '20px'
            }}>
              <CalculatorOutlined />
              ICD-10 NTA Points Data Viewer
              {versionInfo && (
                <Tag color={versionInfo.upload_status === 'active' ? 'green' : 'blue'} style={{ marginLeft: '8px' }}>
                  v{versionInfo.version_number} ({versionInfo.upload_status})
                </Tag>
              )}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {versionId && versionInfo 
                ? `Viewing version ${versionInfo.version_number} data (${versionInfo.upload_status}) - ${versionInfo.row_count?.toLocaleString()} records`
                : 'Browse and search through the current ICD-10 NTA points data'
              }
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="small">
              Refresh
            </Button>
          </Space>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Records"
                value={stats?.count || 0}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          {pointsStats && (
            <>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Highest Points"
                    value={pointsStats.maxPoints}
                    precision={2}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<TrophyOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Average Points"
                    value={pointsStats.avgPoints}
                    precision={2}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Lowest Points"
                    value={pointsStats.minPoints}
                    precision={2}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </>
          )}
        </Row>

        <div style={{ marginBottom: '16px' }}>
          <Search
            placeholder="Search by ICD-10 code..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={handleSearch}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ 
              maxWidth: '100%', 
              width: '100%'
            }}
          />
        </div>
      </div>

      <Card style={{ overflow: 'auto' }}>
        <Table
          columns={columns}
          dataSource={ntaPoints}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['25', '50', '100', '200'],
            responsive: true,
          }}
          onChange={handleTableChange}
          scroll={{ 
            x: 'max-content',
            scrollToFirstRowOnChange: true
          }}
          size="small"
        />
      </Card>
    </div>
  );
}; 